'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useSyncExternalStore } from 'react';

// WHETHER THE PAGE SHOULD RENDER ITS NON-WEBGPU FALLBACK, and why.
//
// Two reasons, and only one of them outlives the page:
//
// - 'unsupported': the browser said no -- no `navigator.gpu`, no adapter,
//   or a request that never settled. That will not change while the tab
//   is open, so it is saved to sessionStorage and holds until the tab is
//   closed: every later page skips WebGPU without asking again.
// - 'fatal': WebGPU came up but then failed -- init hung or threw, the
//   device was lost, a GPU error, a stalled render loop. Usually
//   transient (a busy or restarting GPU process; a refresh tends to fix
//   it), so it is held IN MEMORY ONLY. This page drops to the fallback at
//   once rather than retrying, and a reload tries WebGPU again -- nobody
//   is kept off the full experience for a failure that may not repeat.
export type WebGPUFallbackReason = 'unsupported' | 'fatal';
const STORAGE_KEY = 'webgpu-fallback-mode';
const STORED_UNSUPPORTED = 'unsupported';

// `undefined` until first read. The in-memory copy is what every hook
// renders from, so the answer is stable for the life of the page. It also
// covers the current page when storage is unavailable (private mode,
// blocked site data).
let current: WebGPUFallbackReason | null | undefined;
const listeners = new Set<() => void>();
const readStorage = (): WebGPUFallbackReason | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === STORED_UNSUPPORTED) return 'unsupported';

    // Anything else -- an older format, a hand edit -- is not ours to act
    // on. Cleared, so it cannot keep anyone on the fallback.
    if (stored !== null) sessionStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
};
const getReason = (): WebGPUFallbackReason | null => {
  if (current === undefined) current = readStorage();
  return current;
};
const setReason = (reason: WebGPUFallbackReason) => {
  if (current === reason) return;
  current = reason;
  listeners.forEach(listener => listener());
};

/** The browser cannot run WebGPU. Saved until the session ends. */
export const markWebGPUUnsupported = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, STORED_UNSUPPORTED);
  } catch {
    // Quota or private mode: the in-memory copy still holds for this page.
  }
  setReason('unsupported');
};

/**
 * WebGPU came up and then failed. This page only -- never saved, so a
 * reload tries again. Never downgrades 'unsupported', the stronger one.
 */
export const markWebGPUFatal = (): void => {
  if (getReason() === 'unsupported') return;
  setReason('fatal');
};

/** Non-React read, e.g. to skip an adapter request altogether. */
export const getWebGPUFallbackReason = (): WebGPUFallbackReason | null => getReason();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export interface WebGPUFallbackMode {
  isFallbackMode: boolean;
  reason: WebGPUFallbackReason | 'reduced-motion' | null;
}

/**
 * Whether the page should render its non-WebGPU fallback.
 *
 * Shared by every caller: once any canvas fails, every component using
 * this hook switches together. The server, and the first client render
 * during hydration, always answer "not fallback" -- sessionStorage does
 * not exist there -- and the real answer follows straight after.
 */
export const useWebGPUFallbackMode = () => {
  const $ = _c(3);
  const isReducedMotion = useReducedMotion();
  const reason = useSyncExternalStore(subscribe, getReason, _temp);
  const t0 = isReducedMotion || reason !== null;
  const t1 = isReducedMotion ? "reduced-motion" : reason;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      isFallbackMode: t0,
      reason: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
function _temp() {
  return null;
}